import { CdkTrapFocus } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { MatMenuTrigger } from "@angular/material/menu";
import { MatRadioChange } from "@angular/material/radio";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import subDays from "date-fns/subDays";
import { Subscription } from "rxjs";
import { SatCalendar } from "saturn-datepicker";
import { translateAndFormat } from "src/app/i18next";
import { DateRange, DateRangeFilterState } from "src/app/models/date-range.models";
import { MemCacheService } from "src/app/services/mem-cache/mem-cache.service";
import { newDate } from "src/utils/newDate/newDate";
import { DateRangeFilterChange } from "../../models/emitter-events.models";

@Component({
	selector: "app-date-range-filter",
	templateUrl: "./date-range-filter.component.html",
	styleUrls: ["./date-range-filter.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class DateRangeFilterComponent implements OnInit, OnDestroy {
	instanceId = Math.random().toString(36).substring(7);
	calendarLabelId = `date-range-calendar-label-${this.instanceId}`;
	state: DateRangeFilterState;
	stateKey: string;

	dateRange = DateRange;

	currentFilter: DateRange = DateRange.previous7;
	filterButtonText: string = translateAndFormat("prev 7 days", "capitalize");
	potentialFilterButtonText: string = translateAndFormat("prev 7 days", "capitalize");
	buttonIconValue = "arrow_drop_down";
	filterButtonClass = "filter-button-not-focused";
	filterButtonLabelClass = "filter-button-label-not-focused";

	@ViewChild("calendar", { static: true }) calendar: SatCalendar<Date>;
	@ViewChild(MatMenuTrigger, { static: false }) matMenuTrigger: MatMenuTrigger;
	@ViewChild(CdkTrapFocus) trapFocus: CdkTrapFocus;
	@ViewChild("dialogWrapper") dialogWrapperRef: ElementRef<HTMLElement>;

	maxDate: Date = newDate();
	dateRangeBeginDate: Date = newDate();
	dateRangeEndDate: Date = newDate();

	potentialDateRangeBeginDate: Date = newDate();
	potentialDateRangeEndDate: Date = newDate();

	@Input() initStartDate: Date = startOfDay(subDays(newDate(), 7));
	@Input() initEndDate: Date = newDate();
	@Input() context: string;

	@Output() submitDateRangeFilter = new EventEmitter<DateRangeFilterChange>();
	@Output() radioButtonChange = new EventEmitter<string>();

	defaultState: DateRangeFilterState = {
		selectedDateRange: DateRange.previous7,
		dateRangeBeginDate: this.initStartDate,
		dateRangeEndDate: this.initEndDate,
		potentialDateRangeBeginDate: this.initStartDate,
		potentialDateRangeEndDate: this.initEndDate,
		potentialFilterButtonText: translateAndFormat("prev 7 days", "capitalize"),
		currentFilter: DateRange.previous7,
		filterButtonText: translateAndFormat("prev 7 days", "capitalize"),
	};
	subscriptions = new Subscription();
	// we could also provide events for calendarClicked and cancel

	constructor(
		public myElement: ElementRef,
		public memCacheService: MemCacheService,
		@Inject(DOCUMENT) private readonly document: Document,
	) {}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	initState() {
		this.state = this.getState();
		if (!this.state) {
			this.state = {
				...this.defaultState,
			};
		}

		this.dateRangeBeginDate = this.state.dateRangeBeginDate;
		this.dateRangeEndDate = this.state.dateRangeEndDate;
		this.potentialDateRangeBeginDate = this.state.potentialDateRangeBeginDate;
		this.potentialDateRangeEndDate = this.state.potentialDateRangeEndDate;
		this.filterButtonText = this.state.filterButtonText;
		this.currentFilter = this.state.currentFilter;
		this.potentialFilterButtonText = this.state.potentialFilterButtonText;
		this.state.selectedDateRange = this.state.currentFilter;

		// update calendar
		if (this.calendar) {
			this.calendar.beginDate = this.potentialDateRangeBeginDate;
			this.calendar.endDate = this.potentialDateRangeEndDate;
		}
	}

	ngOnInit() {
		this.stateKey = `date-range-filter: ${this.context}`;

		// listen for clear event
		this.subscriptions.add(
			this.memCacheService.cleared$().subscribe(() => {
				this.memCacheService.setValue(this.stateKey, null);

				this.initState();

				this.filterSubmit();
			}),
		);

		// initialize from persisted state
		this.initState();
	}

	flushState() {
		this.memCacheService.setValue<DateRangeFilterState>(this.stateKey, {
			...this.state,
			dateRangeBeginDate: this.dateRangeBeginDate,
			dateRangeEndDate: this.dateRangeEndDate,
			potentialDateRangeBeginDate: this.potentialDateRangeBeginDate,
			potentialDateRangeEndDate: this.potentialDateRangeEndDate,
			potentialFilterButtonText: this.potentialFilterButtonText,
			currentFilter: this.currentFilter,
			filterButtonText: this.filterButtonText,
		});
	}

	getState() {
		return this.memCacheService.getValue<DateRangeFilterState>(this.stateKey);
	}

	// custom range selected
	inlineRangeChange() {
		this.potentialDateRangeBeginDate = this.calendar.beginDate;
		this.potentialDateRangeEndDate = endOfDay(this.calendar.endDate);
		this.currentFilter = DateRange.custom;
		this.potentialFilterButtonText = translateAndFormat("custom", "capitalize");
		this.state.selectedDateRange = DateRange.custom;
	}

	// when the calendar is interacted with keyboard
	inlineRangeKeydown(event: KeyboardEvent) {
		this.potentialFilterButtonText = translateAndFormat("custom", "capitalize");
		this.state.selectedDateRange = DateRange.custom;
		event.stopPropagation();
	}

	// when the calendar is clicked
	inlineRangeClick(event: MouseEvent) {
		// get ready to change the button value
		this.potentialFilterButtonText = translateAndFormat("custom", "capitalize");

		// move the radio button selection to 'custom'
		this.state.selectedDateRange = DateRange.custom;

		// and prevent the menu from collapsing
		event.stopPropagation();
	}

	matRadioChange(event: MatRadioChange) {
		switch (event.value) {
			case DateRange.today:
				this.potentialDateRangeBeginDate = startOfDay(newDate());
				this.potentialDateRangeEndDate = newDate();
				this.radioButtonChange.emit("today");
				this.currentFilter = DateRange.today;
				this.potentialFilterButtonText = translateAndFormat("today", "capitalize");
				break;

			case DateRange.previous7:
				this.potentialDateRangeBeginDate = startOfDay(subDays(newDate(), 7));
				this.potentialDateRangeEndDate = newDate();
				this.radioButtonChange.emit(DateRange.previous7);
				this.currentFilter = DateRange.previous7;
				this.potentialFilterButtonText = translateAndFormat("prev 7 days", "capitalize");
				break;

			case DateRange.previous14:
				this.potentialDateRangeBeginDate = startOfDay(subDays(newDate(), 14));
				this.potentialDateRangeEndDate = newDate();
				this.radioButtonChange.emit(DateRange.previous14);
				this.currentFilter = DateRange.previous14;
				this.potentialFilterButtonText = translateAndFormat("prev 14 days", "capitalize");
				break;

			case DateRange.previous30:
				this.potentialDateRangeBeginDate = startOfDay(subDays(newDate(), 30));
				this.potentialDateRangeEndDate = newDate();
				this.radioButtonChange.emit(DateRange.previous30);
				this.currentFilter = DateRange.previous30;
				this.potentialFilterButtonText = translateAndFormat("prev 30 days", "capitalize");
				break;

			case DateRange.previous365:
				this.potentialDateRangeBeginDate = startOfDay(subDays(newDate(), 365));
				this.potentialDateRangeEndDate = newDate();
				this.radioButtonChange.emit(DateRange.previous365);
				this.currentFilter = DateRange.previous365;
				this.potentialFilterButtonText = translateAndFormat("prev 365 days", "capitalize");
				break;

			case DateRange.custom:
				this.radioButtonChange.emit(DateRange.custom);
				this.currentFilter = DateRange.custom;
				this.potentialFilterButtonText = translateAndFormat("custom", "capitalize");
				break;
		}

		this.state.selectedDateRange = this.currentFilter;
	}

	matRadioClick(event: MouseEvent): void {
		event.stopPropagation(); // otherwise the menu will collapse
	}

	filterSubmit(): void {
		this.memCacheService.setValue("resetState", true);
		const calendarBeginDate = this.calendar.beginDate;
		const calendarEndDate = this.calendar.endDate;

		const dateEvent: DateRangeFilterChange = [
			{
				startTime: calendarBeginDate,
				endTime: calendarEndDate,
			},
		];

		this.submitDateRangeFilter.emit(dateEvent);
		this.matMenuTrigger.closeMenu();

		// don't apply the change to the control until the user clicks 'apply'
		this.filterButtonText = this.potentialFilterButtonText;
		this.dateRangeBeginDate = this.potentialDateRangeBeginDate;
		this.dateRangeEndDate = this.potentialDateRangeEndDate;

		this.flushState();
	}

	filterCancel() {
		this.matMenuTrigger.closeMenu();
	}

	handleMenuClose(): void {
		this.buttonIconValue = "arrow_drop_down";
		this.filterButtonClass = "filter-button-not-focused";
		this.filterButtonLabelClass = "filter-button-label-not-focused";
	}

	handleMenuOpen(): void {
		this.buttonIconValue = "arrow_drop_up";
		this.filterButtonClass = "filter-button-focused";
		this.filterButtonLabelClass = "filter-button-label-focused";
		this.applyMenuPanelAria();

		// reset the calendar
		this.potentialDateRangeBeginDate = this.dateRangeBeginDate;
		this.potentialDateRangeEndDate = this.dateRangeEndDate;

		// (re) initialize from persisted state
		this.initState();

		// Move keyboard focus into the trap after the overlay has settled
		setTimeout(() => {
			const wrapper = this.dialogWrapperRef?.nativeElement;
			if (wrapper) {
				const checkedRadio = wrapper.querySelector("mat-radio-button.mat-radio-checked input") as HTMLElement;
				if (checkedRadio) {
					checkedRadio.focus();
					return;
				}
				this.trapFocus?.focusTrap?.focusFirstTabbableElementWhenReady();
			}
		});
	}

	handleMenuKeydown(event: KeyboardEvent) {
		if (event.key !== "Tab") {
			// Stop Material Menu from catching arrows/space/enter and closing or navigating.
			const actionKeys = [" ", "Enter", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
			if (actionKeys.includes(event.key)) {
				event.stopPropagation();
			}
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		const wrapper = this.dialogWrapperRef?.nativeElement;
		if (!wrapper) return;

		const focusable: HTMLElement[] = [];
		this.collectFocusableElements(wrapper, focusable);

		if (!focusable.length) return;

		const activeEl = this.getDeepActiveElement();
		const currentIndex = focusable.indexOf(activeEl as HTMLElement);

		if (event.shiftKey) {
			const prev = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
			focusable[prev].focus();
		} else {
			const next = currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1;
			focusable[next].focus();
		}
	}

	/**
	 * Walks the DOM tree in document order, recursing into Shadow roots so that
	 * focusable elements inside ShadowDom-encapsulated components (e.g. app-action-button)
	 * are included in the tab sequence.
	 */
	private collectFocusableElements(node: Element | ShadowRoot, result: HTMLElement[]): void {
		for (const child of Array.from(node.children)) {
			if (this.isFocusableElement(child as HTMLElement)) {
				result.push(child as HTMLElement);
			}
			if ((child as Element).shadowRoot) {
				this.collectFocusableElements((child as Element).shadowRoot, result);
			} else {
				this.collectFocusableElements(child as Element, result);
			}
		}
	}

	/**
	 * Returns true when an element should participate in the manual Tab sequence.
	 */
	private isFocusableElement(el: HTMLElement): boolean {
		if ((el as HTMLButtonElement).disabled) return false;
		const tabindex = el.getAttribute("tabindex");
		if (tabindex === "-1") return false;
		if (el.classList.contains("cdk-focus-trap-anchor")) return false;
		const tag = el.tagName.toLowerCase();
		if (["button", "input", "select", "textarea"].includes(tag)) return true;
		if (tag === "a" && el.hasAttribute("href")) return true;
		if (tabindex !== null) return true;
		return false;
	}

	/**
	 * Traverses shadow roots to find the truly focused element.
	 * document.activeElement only returns the shadow host when focus is inside a
	 * Shadow DOM — this helper drills down to the actual focused element.
	 */
	private getDeepActiveElement(): Element | null {
		let el: Element | null = document.activeElement;
		while (el?.shadowRoot?.activeElement) {
			el = el.shadowRoot.activeElement;
		}
		return el;
	}

	private applyMenuPanelAria() {
		setTimeout(() => {
			const panelId = this.matMenuTrigger?.menu?.panelId;
			const panel = panelId ? (this.document.getElementById(panelId) as HTMLElement | null) : null;
			if (!panel) {
				return;
			}

			// This popup contains form controls (radio group + calendar + buttons),
			// so it should not be exposed as an ARIA menu.
			panel.setAttribute("role", "dialog");
			panel.setAttribute("aria-modal", "false");
			panel.setAttribute("aria-label", translateAndFormat("date range", "title"));

			// Saturn/Material calendar generates an empty <th> divider header which WAVE flags.
			// Ensure it contains non-visible text so it is not an "empty table header".
			this.patchEmptyCalendarHeader(panel);
		});
	}

	private patchEmptyCalendarHeader(panel: HTMLElement) {
		setTimeout(() => {
			const headerDividerCells = panel.querySelectorAll<HTMLTableCellElement>(
				"th.mat-calendar-table-header-divider",
			);
			headerDividerCells.forEach(th => {
				const alreadyPatched = !!th.querySelector(".cdk-visually-hidden");
				const hasText = (th.textContent || "").trim().length > 0;
				if (alreadyPatched || hasText) {
					return;
				}

				const hiddenText = this.document.createElement("span");
				hiddenText.className = "cdk-visually-hidden";
				hiddenText.textContent = "divider";
				th.appendChild(hiddenText);
			});
		});
	}
}
